import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Autocomplete, Box, Card, Modal, TextField } from '@mui/material';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { OptionType } from '@frontend/features/common/types';

import { useGetUsersByTokensAgency } from '../../../users/queries';

interface Props {
  isOpen: boolean;
  teamId: number;
  onClose: () => void;
  queryKey?: unknown;
}

export const LinkUserToTeam = ({ isOpen, onClose, teamId, queryKey }: Props) => {
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const [autocompleteValue, setAutocompleteValue] = useDebouncedState<string>('', 500);
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const { data: userList } = useGetUsersByTokensAgency({
    pageSize: 10,
    currentPage: 1,
    search: autocompleteValue,
  });

  const listOptionscaseAgentListOption =
    userList?.items?.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName} - ${user.email}`,
    })) ?? [];

  const handleChange = async (userItem: OptionType | null) => {
    try {
      await client.post(`/team/${teamId}/users/${userItem?.value}`);

      addToast({
        message: t('teams.linkUserToTeam.success'),
        color: 'success',
      });

      queryKey && queryClient.invalidateQueries(queryKey);
    } catch (_e) {
      addToast({
        message: t('teams.linkUserToTeam.success'),
        color: 'error',
      });
    } finally {
      onClose();
    }
  };

  const handleCancel = () => {
    setAutocompleteValue('');
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: 800,
          p: 2,
        }}
        elevation={4}
      >
        <Box ref={formRef} component="form" display="flex" gap={2}>
          <Autocomplete
            fullWidth
            options={listOptionscaseAgentListOption}
            getOptionLabel={(option) => option.label}
            onChange={(_event: React.SyntheticEvent, newValue: OptionType | null) => {
              handleChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                onChange={(e) => {
                  setAutocompleteValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  e.nativeEvent.key === 'Escape' && handleCancel();
                }}
                variant="outlined"
              />
            )}
          />
        </Box>
      </Card>
    </Modal>
  );
};
